// eslint-disable-next-line import/no-extraneous-dependencies
import Mark from 'mark.js';

/**
 * Search component.
 *
 * @param {HTMLElement} searchElement
 */
export function Search(searchElement) {
  const resetButton = searchElement.querySelector('.search__reset-button');
  const searchInput = searchElement.querySelector('.search__input');
  const searchResultsContainer = document.getElementById('search-results');
  const searchResultsSection = searchResultsContainer.querySelectorAll('[data-search-result-section]');
  const searchResultsHeading = searchResultsContainer.querySelector('.search-results__heading');
  const searchResultsHeadingPlaceholder = searchResultsHeading.dataset.placeholder || 'Search results for "%query%"';
  const searchableContents = searchResultsContainer.querySelectorAll('[data-is-searchable]');
  const highlighter = new Mark(searchableContents);

  /**
   * Generic for staggering actions (e.g. search on key up events).
   *
   * @param func
   *   Action that happens after timeout/delay (e.g. search).
   * @param timeout
   *   Timeout in ms to wait for until search starts.
   * @returns {(function(...[*]): void)|*}
   */
  const debounce = (func, timeout = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
  };

  /**
   * Updates the search results heading with the query.
   *
   * @param {string} query
   *   The keyword used for the search.
   */
  const updateSearchResultHeading = (query) => {
    searchResultsHeading.textContent = searchResultsHeadingPlaceholder.replace('%query%', query);
    searchResultsHeading.style.display = query ? 'block' : 'none';
  };

  /**
   * Resets the search results and closes all accordions.
   */
  const reset = () => {
    const headings = searchResultsContainer.querySelectorAll('[data-search-result-section-heading]');
    const searchResults = searchResultsContainer.querySelectorAll('[data-search-result]');

    headings.forEach((heading) => {
      // eslint-disable-next-line no-param-reassign
      heading.style.display = 'block';
    });

    searchResults.forEach((searchResult) => {
      searchResult.removeAttribute('open');
      // eslint-disable-next-line no-param-reassign
      searchResult.style.display = 'block';
    });
  };

  /**
   * Performs an on-page search, show and expands hits and hides everything else.
   *
   * @param query
   *   The keyword used for the search.
   */
  const search = (query) => {
    const pattern = new RegExp(query, 'gim');

    searchResultsSection.forEach((section) => {
      const heading = section.querySelector('[data-search-result-section-heading]');
      const searchResults = section.querySelectorAll('[data-search-result]');
      let showSection = false;

      searchResults.forEach((searchResult) => {
        const searchable = searchResult.querySelectorAll('[data-is-searchable]');
        let show = false;

        for (let i = 0; i < searchable.length; i++) {
          const searchableContent = searchable[i];
          const text = searchableContent.textContent;

          if (pattern.test(text)) {
            show = true;
            showSection = true;
          }
        }

        // eslint-disable-next-line no-param-reassign
        searchResult.style.display = show ? 'block' : 'none';
        if (show) {
          searchResult.setAttribute('open', true);
        } else {
          searchResult.removeAttribute('open');
        }
      });

      heading.style.display = showSection ? 'block' : 'none';
    });
  };

  /**
   * Event handler for reset (e.g. reset button is clicked)
   */
  const onReset = () => {
    searchElement.reset();
    highlighter.unmark();
    reset();
    document.querySelector('body').classList.remove('search--active');
    updateSearchResultHeading('');
  };

  /**
   * Event handler for all submit events (e.g. enter key, submit button click).
   *
   * @param e
   *   The event.
   */
  const onSearch = (e) => {
    e.preventDefault();
    const query = searchInput.value;

    highlighter.unmark();

    if (query) {
      highlighter.mark(query);
      document.querySelector('body').classList.add('search--active');
      search(query);
    } else {
      reset();
      document.querySelector('body').classList.remove('search--active');
    }
    updateSearchResultHeading(query);
  };

  /**
   * Initialize our component.
   */
  const init = () => {
    resetButton.addEventListener('click', onReset);
    searchElement.addEventListener('keyup', debounce(onSearch, 500));
    searchElement.addEventListener('submit', onSearch);
    document.addEventListener('toc-link-clicked', onReset);
  };

  init();
}
